import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '@utils/axios';
import {
  inspectionsApiInternalDashboardPath,
  workOrdersApiInternalDashboardPath,
  deficienciesApiInternalDashboardPath,
  proposalsApiInternalDashboardPath,
  paymentsApiInternalDashboardPath,
  invoicesApiInternalDashboardPath,
  highPriorityApiInternalDashboardPath
} from 'routes';
import { DateTime } from 'luxon';
import { ActionTypes, reducer } from './reducer';
import { BaseStatusesSelectionSort, DashboardEntityType, InitialState } from './constants';
import { Cache, CacheKeys } from '../../../../utils/Cache';
import getSortedStatuses from '../utils/getSortedStatuses';
import { BaseOtherStatus } from '../nested/StatusCounterWidget/constants';

export const HomeDataTableContext = React.createContext({
  state: {
    entityType: null,
    tableData: [],
    isModalOpen: false,
    columnsStructureData: [],
    status: null,
    inspectionStatuses: [],
    workOrderStatuses: [],
    statusesSelection: {
      inspections: [],
      workOrders: []
    }
  },
  actions: {
    loadInspections: () => {},
    closeModal: () => {},
    changeStatusesSelection: () => {},
    loadWorkOrders: () => {},
    loadInvoices: () => {},
    loadPayments: () => {},
    loadProposals: () => {},
    loadDeficiencies: () => {},
    loadHighPriorities: () => {}
  }
});

export function HomeDataTableProvider({
  inspectionStatuses,
  workOrderStatuses,
  territoryFilter,
  workOrderDateRange,
  children
}) {
  const [state, dispatch] = React.useReducer(reducer, {
    ...InitialState.state,
    inspectionStatuses: getSortedStatuses(inspectionStatuses, 'inspections'),
    workOrderStatuses: getSortedStatuses(workOrderStatuses, 'workOrders')
  });

  const getTerritoryIds = () => {
    return territoryFilter && territoryFilter.length > 0 ? territoryFilter.map((t) => t.value || t) : undefined;
  };

  const loadInspections = async ({ status, isToday }) => {
    dispatch({
      type: ActionTypes.START_LOADING,
      payload: { entityType: DashboardEntityType.Inspections, statusSelected: status, isToday }
    });
    const params = {
      status:
        status === BaseOtherStatus.statusCode
          ? inspectionStatuses
              .filter((s) => !state.statusesSelection.inspections.includes(s.statusCode))
              .map((s) => s.statusCode)
          : status,
      start_date: isToday ? DateTime.local().startOf('day').toISODate() : DateTime.local().startOf('month').toISODate(),
      end_date: isToday ? DateTime.local().endOf('day').toISODate() : DateTime.local().endOf('month').toISODate(),
      territory_ids: getTerritoryIds()
    };

    const { data: res } = await api.get(inspectionsApiInternalDashboardPath({ format: 'json' }), { params });
    dispatch({
      type: ActionTypes.LOAD_DATA,
      payload: { entityType: DashboardEntityType.Inspections, tableData: res }
    });
  };

  const loadWorkOrders = async ({ status, isToday, allVisits }) => {
    dispatch({
      type: ActionTypes.START_LOADING,
      payload: { entityType: DashboardEntityType.WorkOrders, statusSelected: status, isToday }
    });

    let work_order_start_date;
    let work_order_end_date;
    let work_order_all_time;

    if (isToday) {
      work_order_start_date = DateTime.local().startOf('day').toISODate();
      work_order_end_date = DateTime.local().endOf('day').toISODate();
    } else if (workOrderDateRange?.start == null && workOrderDateRange?.end == null) {
      // all-time preset: omit the scheduled_date predicate entirely
      work_order_all_time = 'true';
    } else {
      work_order_start_date = workOrderDateRange?.start
        ? DateTime.fromJSDate(workOrderDateRange.start).toISODate()
        : undefined;
      work_order_end_date = workOrderDateRange?.end
        ? DateTime.fromJSDate(workOrderDateRange.end).toISODate()
        : undefined;
    }

    const params = {
      status:
        status === BaseOtherStatus.statusCode
          ? workOrderStatuses
              .filter((s) => !state.statusesSelection.workOrders.includes(s.statusCode))
              .map((s) => s.statusCode)
          : status,
      work_order_start_date,
      work_order_end_date,
      work_order_all_time,
      template: allVisits === true ? 'visits' : null,
      territory_ids: getTerritoryIds()
    };

    const { data: res } = await api.get(workOrdersApiInternalDashboardPath({ format: 'json' }), { params });
    dispatch({
      type: ActionTypes.LOAD_DATA,
      payload: { entityType: DashboardEntityType.WorkOrders, tableData: res }
    });
  };

  const loadHighPriorities = async () => {
    dispatch({
      type: ActionTypes.START_LOADING,
      payload: { entityType: DashboardEntityType.HighPriorities }
    });

    const params = { territory_ids: getTerritoryIds() };
    const { data: res } = await api.get(highPriorityApiInternalDashboardPath({ format: 'json' }), { params });
    dispatch({
      type: ActionTypes.LOAD_DATA,
      payload: { entityType: DashboardEntityType.HighPriorities, tableData: res }
    });
  };

  const loadInvoices = async ({ status }) => {
    const entityType = status === 'paid' ? DashboardEntityType.PaidInvoices : DashboardEntityType.Invoices;
    dispatch({
      type: ActionTypes.START_LOADING,
      payload: { entityType, statusSelected: status }
    });

    const params = {
      status,
      start_date: DateTime.local().minus({ days: 30 }).toISODate(),
      end_date: DateTime.local().toISODate(),
      territory_ids: getTerritoryIds()
    };
    const { data: res } = await api.get(invoicesApiInternalDashboardPath({ format: 'json' }), { params });
    dispatch({
      type: ActionTypes.LOAD_DATA,
      payload: { entityType, tableData: res }
    });
  };

  const loadPayments = async () => {
    dispatch({
      type: ActionTypes.START_LOADING,
      payload: { entityType: DashboardEntityType.Payments }
    });
    const params = {
      start_date: DateTime.local().minus({ days: 30 }).toISODate(),
      end_date: DateTime.local().toISODate(),
      territory_ids: getTerritoryIds()
    };
    const { data: res } = await api.get(paymentsApiInternalDashboardPath({ format: 'json' }), { params });

    dispatch({
      type: ActionTypes.LOAD_DATA,
      payload: { entityType: DashboardEntityType.Payments, tableData: res }
    });
  };

  const loadProposals = async ({ status }) => {
    dispatch({
      type: ActionTypes.START_LOADING,
      payload: { entityType: DashboardEntityType.Proposals, statusSelected: status }
    });
    const params = {
      status,
      start_date: DateTime.local().minus({ days: 30 }).toISODate(),
      end_date: DateTime.local().toISODate(),
      territory_ids: getTerritoryIds()
    };
    const { data: res } = await api.get(proposalsApiInternalDashboardPath({ format: 'json' }), { params });

    dispatch({
      type: ActionTypes.LOAD_DATA,
      payload: { entityType: DashboardEntityType.Proposals, tableData: res }
    });
  };

  const loadDeficiencies = async ({ status }) => {
    dispatch({
      type: ActionTypes.START_LOADING,
      payload: { entityType: DashboardEntityType.Deficiencies, statusSelected: status }
    });
    const params = {
      status,
      start_date: DateTime.local().minus({ days: 30 }).toISODate(),
      end_date: DateTime.local().toISODate(),
      territory_ids: getTerritoryIds()
    };
    const { data: res } = await api.get(deficienciesApiInternalDashboardPath({ format: 'json' }), { params });

    dispatch({
      type: ActionTypes.LOAD_DATA,
      payload: { entityType: DashboardEntityType.Deficiencies, tableData: res }
    });
  };

  // Soft dashboard refetches pass new status counts via props; sync them into reducer
  // state so StatusCounterWidget does not keep the first-fetch totals.
  useEffect(() => {
    dispatch({
      type: ActionTypes.SYNC_WIDGET_STATUSES,
      payload: {
        inspectionStatuses: getSortedStatuses(inspectionStatuses, 'inspections'),
        workOrderStatuses: getSortedStatuses(workOrderStatuses, 'workOrders')
      }
    });
  }, [inspectionStatuses, workOrderStatuses]);

  useEffect(() => {
    const statusesSelection = Cache.getItem(CacheKeys.StatusesSelection);
    if (statusesSelection) {
      dispatch({ type: ActionTypes.LOAD_STATUSES_SELECTION, payload: { statusesSelection } });
    } else {
      Cache.setItem(CacheKeys.StatusesSelection, BaseStatusesSelectionSort);
      changeStatusesSelection(BaseStatusesSelectionSort);
    }
  }, []);

  const closeModal = () => {
    dispatch({ type: ActionTypes.CLOSE_MODAL });
  };

  const changeStatusesSelection = async (statusesSelection) => {
    Cache.setItem(CacheKeys.StatusesSelection, statusesSelection);
    dispatch({ type: ActionTypes.LOAD_STATUSES_SELECTION, payload: { statusesSelection } });
  };

  const contextValue = useMemo(() => {
    return {
      state,
      actions: {
        loadInspections,
        loadHighPriorities,
        closeModal,
        changeStatusesSelection,
        loadWorkOrders,
        loadInvoices,
        loadPayments,
        loadProposals,
        loadDeficiencies
      }
    };
    // workOrderDateRange is included so loadWorkOrders always closes over the current window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, dispatch, workOrderDateRange]);

  return <HomeDataTableContext.Provider value={contextValue}>{children}</HomeDataTableContext.Provider>;
}

HomeDataTableProvider.propTypes = {
  children: PropTypes.node,
  inspectionStatuses: PropTypes.array,
  workOrderStatuses: PropTypes.array,
  /** Active date window for the Service widget; passed into loadWorkOrders for modal parity. */
  workOrderDateRange: PropTypes.shape({
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date)
  })
};
HomeDataTableProvider.defaultProps = {
  children: null,
  inspectionStatuses: [],
  workOrderStatuses: [],
  workOrderDateRange: null
};
